// src/components/tasks/ProductionTaskCard.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { parseUTC } from '../../utils/dateUtils';
import {
  Package,
  Clock,
  CheckCircle,
  Timer,
  AlertTriangle,
  Factory,
  FileText,
  QrCode,
  Hash,
  Calendar,
  BarChart3,
  User,
  Users,
  Activity,
  ChevronRight,
  Layers,
  Tag
} from 'lucide-react';

const ProductionTaskCard = ({ task, onClick, expanded = false, isRunning = false }) => {
  const { t, i18n } = useTranslation();

  // Get status styling — derived from ProcessingStatusCode + running timer
  const getStatusConfig = (status) => {
    const configs = {
      "completed": {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-700 dark:text-green-400',
        icon: <CheckCircle className="w-4 h-4" />,
        label: t('pages.productionTasks.completed')
      },
      "in-progress" : {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-700 dark:text-blue-400',
        icon: <Clock className="w-4 h-4" />,
        label: t('pages.productionTasks.inProgress')
      },
      "not-started": {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        text: 'text-amber-700 dark:text-amber-400',
        icon: <Timer className="w-4 h-4" />,
        label: t('pages.productionTasks.notStarted')
      }
      // 'not-started': {
      //   bg: 'bg-gray-50 dark:bg-gray-700',
      //   border: 'border-gray-200 dark:border-gray-600',
      //   text: 'text-gray-700 dark:text-gray-300',
      //   icon: <Timer className="w-4 h-4" />,
      //   label: 'Not Started'
      // }
    };
    return configs[status] || configs['1'];
  };

  // Derive display status from ProcessingStatusCode + running timer
  const deriveStatus = () => {
    if (task.dbProductionTasks[0].processingStatusCode === 'completed' || task.dbProductionTasks[0].processingStatusCode === "3") return 'completed';
    if (task.dbProductionTasks[0].processingStatusCode === 'in-progress' || task.dbProductionTasks[0].processingStatusCode === "2") return 'in-progress';
    if (task.dbProductionTasks[0].processingStatusCode === 'not-started' || task.dbProductionTasks[0].processingStatusCode === "1") return 'not-started';
    return 'not-started';
  };

  const statusConfig = getStatusConfig(deriveStatus());

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('pages.productionTasks.notSet');
    try {
      return parseUTC(dateString).toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Calculate progress: API value + running timer override
  const calculateProgress = () => {
    if (task.dbProductionTasks[0].processingStatusCode === 'completed' || task.dbProductionTasks[0].processingStatusCode === "3") return 100;
    if (isRunning) return 50;
    if (task.progress != null && task.progress > 0) return task.progress;
    if (task.dbProductionTasks[0].processingStatusCode === 'in-progress' || task.dbProductionTasks[0].processingStatusCode === "2") return 50;
    if (task.dbProductionTasks[0].processingStatusCode === 'not-started' || task.dbProductionTasks[0].processingStatusCode === "1") return 0;
    if (task.totalQuantity && task.totalCompletedQuantity) {
      return Math.round((task.totalCompletedQuantity / task.totalQuantity) * 100);
    }
    return 0;
  };

  const progress = calculateProgress();

  // Helper function to get the productionTaskId from dbProductionTasks
  const getProductionTaskId = () => {
    const dbTasks = task.dbProductionTasks || task.productionTasks || [];
    if (dbTasks.length > 0 && dbTasks[0].productionTaskId) {
      return dbTasks[0].productionTaskId;
    }
    return null;
  };

  // Helper function to get assignment display text
  const getAssignmentDisplay = () => {
    // Check both possible property names
    const productionTasks = task.dbProductionTasks || task.productionTasks || [];

    if (productionTasks.length > 0) {
      // Collect all assignments from all production tasks
      const allAssignments = productionTasks.flatMap(pt => pt.assignments || []);

      if (allAssignments.length === 0) return t('pages.productionTasks.unassigned');

      // Get unique user IDs - filter out null, undefined, and empty strings
      const uniqueUserIds = [...new Set(
        allAssignments
          .map(a => a.userId)
          .filter(id => id !== null && id !== undefined && id !== "")
      )];

      // Get unique team IDs - filter out null, undefined, and empty strings
      const uniqueTeamIds = [...new Set(
        allAssignments
          .map(a => a.teamId)
          .filter(id => id !== null && id !== undefined && id !== "")
      )];

      // Build display string
      const displayParts = [];

      if (uniqueUserIds.length > 0) {
        if (uniqueUserIds.length === 1) {
          displayParts.push(t('pages.productionTasks.oneUser'));
        } else {
          displayParts.push(t('pages.productionTasks.usersCount', { count: uniqueUserIds.length }));
        }
      }

      if (uniqueTeamIds.length > 0) {
        if (uniqueTeamIds.length === 1) {
          displayParts.push(t('pages.productionTasks.oneTeam'));
        } else {
          displayParts.push(t('pages.productionTasks.teamsCount', { count: uniqueTeamIds.length }));
        }
      }

      if (displayParts.length > 0) {
        return displayParts.join(', ');
      }
    }

    return t('pages.productionTasks.unassigned');
  };

  // Helper function to check if task has any assignments
  const hasAssignments = () => {
    const productionTasks = task.dbProductionTasks || task.productionTasks || [];

    if (productionTasks.length > 0) {
      const allAssignments = productionTasks.flatMap(pt => pt.assignments || []);

      // Check if there's any valid assignment
      return allAssignments.some(a =>
        (a.userId !== null && a.userId !== undefined && a.userId !== "") ||
        (a.teamId !== null && a.teamId !== undefined && a.teamId !== "")
      );
    }
    return false;
  };

  const productionTaskId = getProductionTaskId();

  if (expanded) {
    // Expanded view for modal
    return (
      <div className="space-y-6">
        {/* Header with IDs - Updated to show productionTaskId as OrderId */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Hash className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('pages.productionTasks.productionLotId')}</span>
            </div>
            <p className="font-mono text-lg font-bold text-blue-600 dark:text-blue-400 truncate">
              {task.productionLotId || task.id || '-'}
            </p>
          </div>

          {/* New card for ProductionTaskId */}
          {productionTaskId && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Layers className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('pages.productionTasks.orderId')}</span>
              </div>
              <p className="font-mono text-lg font-bold text-amber-600 dark:text-amber-400 truncate">
                {productionTaskId}
              </p>
            </div>
          )}

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-xl border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('pages.productionTasks.orderUuid')}</span>
            </div>
            <p className="font-mono text-sm font-bold text-purple-600 dark:text-purple-400 truncate">
              {task.productionOrderId || '-'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Factory className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('pages.productionTasks.site')}</span>
            </div>
            <p className="font-semibold text-gray-900 dark:text-white text-lg truncate">
              {task.siteId || '-'}
            </p>
            {task.siteDescription && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{task.siteDescription}</p>
            )}
          </div>

          {task.salesOrderNo && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 rounded-xl border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <Tag className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('pages.productionTasks.salesOrder')}</span>
              </div>
              <p className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400 truncate">
                {task.salesOrderNo}
              </p>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="mb-3">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <Package className="w-3 h-3" />
            <span>{t('pages.productionTasks.product')}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
              {task.mainOutputProductId || t('pages.productionTasks.notAvailable')}
            </span>
            {task.mainOutputProductDescription && (
              <span className="text-xs text-gray-400 truncate max-w-[120px]">
                {task.mainOutputProductDescription}
              </span>
            )}
          </div>
        </div>

        {/* Quantity Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.totalQuantity')}</span>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {task.totalQuantity ?? '-'} {task.totalQuantityUnit || ''}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.completedQuantity')}</span>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {task.totalCompletedQuantity ?? '-'} {task.totalCompletedQuantityUnit || ''}
            </p>
          </div>
        </div>

        {/* Schedule */}
        {task.productionStartDate && (
          <div className="bg-gradient-to-r from-green-50 to-red-50 dark:from-green-900/20 dark:to-red-900/20 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('pages.productionTasks.productionSchedule')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.startDate')}</span>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(task.productionStartDate)}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.endDate')}</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {task.productionEndDate ? formatDate(task.productionEndDate) : t('pages.productionTasks.notSet')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Barcode */}
        {task.barcodeBase64 && (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              {t('pages.productionTasks.barcode')}
            </h4>
            <div className="flex items-center gap-4">
              <img
                src={`data:image/png;base64,${task.barcodeBase64}`}
                alt={t('pages.productionTasks.barcode')}
                className="h-12 w-auto"
              />
              <span className="font-mono text-gray-900 dark:text-white">{task.barcodeNumber}</span>
            </div>
          </div>
        )}

        {/* Activities */}
        {task.confirmationGroups && task.confirmationGroups.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              {t('pages.productionTasks.activities')}
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {task.confirmationGroups.flatMap(cg => cg.activities || []).map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{activity.operationId}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">{activity.activityDescription}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{activity.plannedDuration}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignment Info - FIXED for expanded view */}
        {hasAssignments() && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
            <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('pages.productionTasks.assignedTo')}
            </h4>

            {/* Check dbProductionTasks first */}
            {task.dbProductionTasks && task.dbProductionTasks.length > 0 && (
              <>
                {/* Get all unique assignments */}
                {(() => {
                  const allAssignments = task.dbProductionTasks.flatMap(pt => pt.assignments || []);

                  // Get unique users (by userId)
                  const uniqueUserIds = [...new Set(allAssignments.map(a => a.userId).filter(id => id))];

                  // Get unique teams (by teamId)
                  const uniqueTeamIds = [...new Set(allAssignments.map(a => a.teamId).filter(id => id))];

                  return (
                    <>
                      {/* Display users */}
                      {uniqueUserIds.map(userId => (
                        <div key={userId} className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('pages.productionTasks.userId')}: {userId.substring(0, 8)}...</span>
                        </div>
                      ))}

                      {/* Display teams */}
                      {uniqueTeamIds.map(teamId => (
                        <div key={teamId} className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('pages.productionTasks.teamId')}: {teamId.substring(0, 8)}...</span>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Compact card view (default)
  return (
    <div
      onClick={onClick}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer relative"
    >
      <div className={`h-1.5 sm:h-2 w-full ${statusConfig.bg} border-b ${statusConfig.border}`}></div>

      {/* Card Content */}
      <div className="p-3 sm:p-5">
        {/* Header with Status */}
        <div className="flex items-start justify-between mb-2 sm:mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xs sm:text-base font-semibold text-gray-900 dark:text-white truncate pr-2 group-hover:text-blue-600 transition-colors">
              {task.title || t('pages.productionTasks.productionLotTitle', { id: task.productionLotId })}
            </h3>
            {/* <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {task.description}
            </p> */}
          </div>
          <div className={`flex items-center gap-1 px-1.5 py-0.5 sm:px-2.5 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border whitespace-nowrap`}>
            {statusConfig.icon}
            <span className="hidden sm:inline">{statusConfig.label}</span>
          </div>
        </div>

        {/* IDs Section - Updated to show productionTaskId */}
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 bg-gray-50 dark:bg-gray-700 p-1.5 sm:p-2 rounded-lg">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              <Hash className="w-3 h-3" />
              <span>{t('commonUi.lot')}</span>
            </div>
            <p className="font-mono text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 truncate">
              {task.productionLotId || task.id || '-'}
            </p>
          </div>
          <div className="w-px h-6 sm:h-8 bg-gray-200 dark:bg-gray-600"></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              <Layers className="w-3 h-3" />
              <span>{t('pages.productionTasks.order')}</span>
            </div>
            <p className="font-mono text-xs sm:text-sm font-medium text-amber-600 dark:text-amber-400 truncate">
              {productionTaskId || '-'}
            </p>
          </div>
          {task.salesOrderNo && (
            <>
              <div className="w-px h-6 sm:h-8 bg-gray-200 dark:bg-gray-600"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                  <Tag className="w-3 h-3" />
                  <span>{t('pages.productionTasks.salesOrder')}</span>
                </div>
                <p className="font-mono text-xs sm:text-sm font-medium text-emerald-600 dark:text-emerald-400 truncate">
                  {task.salesOrderNo}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Product Info */}
        <div className="mb-2 sm:mb-3">
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 sm:mb-1">
            <Package className="w-3 h-3" />
            <span>{t('pages.productionTasks.product')}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
              {task.mainOutputProductId || t('pages.productionTasks.notAvailable')}
            </span>
            {task.mainOutputProductDescription && (
              <span className="text-[10px] sm:text-xs text-gray-400 truncate max-w-[120px]">
                {task.mainOutputProductDescription}
              </span>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-2 sm:mb-3">
          <div className="flex items-center justify-between text-[10px] sm:text-xs mb-1">
            <span className="text-gray-500 dark:text-gray-400">{t('pages.productionTasks.progress')}</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 sm:h-2">
            <div
              className={`h-1.5 sm:h-2 rounded-full transition-all duration-500 ${task.dbProductionTasks[0].processingStatusCode === 'completed' || task.dbProductionTasks[0].processingStatusCode === "3" ? 'bg-green-500' :
                isRunning ? 'bg-blue-500' :
                  task.dbProductionTasks[0].processingStatusCode === 'in-progress' || task.dbProductionTasks[0].processingStatusCode === "2" ? 'bg-amber-500' : 'bg-gray-400'
                }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex items-center gap-1">
              <div className="p-0.5 sm:p-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                <User className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" />
              </div>
              <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                {getAssignmentDisplay()}
              </span>
            </div>

            {/* Show assignment count badge if multiple assignments */}
            {task.dbProductionTasks && task.dbProductionTasks.length > 0 && (
              (() => {
                const allAssignments = task.dbProductionTasks.flatMap(pt => pt.assignments || []);
                const totalAssignments = allAssignments.filter(a => a.userId || a.teamId).length;
                if (totalAssignments > 1) {
                  return (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      {totalAssignments}
                    </span>
                  );
                }
                return null;
              })()
            )}
          </div>
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            <span>{task.createdAt ? formatDate(task.createdAt) : '-'}</span>
          </div>
        </div>

        {/* View Details Indicator */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
            {t('pages.productionTasks.viewDetails')}
            <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductionTaskCard);
